<template>
  <master-layout id="contentResourceSessions">
    <div class="flex flex-row justify-center w-[100vw] px-6">
      <div
        class="w-[100vw] max-w-[856px] mt-4 md:mt-[32px]"
        :class="{ 'pt-12': isIos || isAndroid }"
      >
        <div class="block mb-4">
          <img
            src="../../../public/assets/icons/go-back-chevron.svg"
            alt="Back Icon"
            class="cursor-pointer"
            @click="back()"
          />
        </div>
        <SessionSearch
          :searchTerm="searchTerm"
          :assets="flattenedMaterials"
          @submit-search="onSearch"
        />
        <div v-if="isSearching && !isLoading">
          <p v-if="searchTerm" class="my-6 text-[16px] leading-[28px]">
            {{ searchResultsCopy }}
          </p>
          <div class="font-aktiv-grotesk">
            <!-- PDF -->
            <div v-if="getAssets.pdf.length > 0">
              <ResourceCard
                v-for="(material, idx) in getAssets.pdf"
                :key="idx"
                :resource="material"
                :data-event-tracking="
                  material.title.toLowerCase().replace(/[\s:]+/g, '-')
                "
                class="my-6"
              />
            </div>

            <!-- Videos & articles -->
            <div v-if="getAssets.links.length > 0">
              <ResourceCard
                v-for="(material, idx) in getAssets.links"
                :key="idx"
                :resource="material"
                :data-event-tracking="
                  material.title.toLowerCase().replace(/[\s:]+/g, '-')
                "
                class="my-6"
              />
            </div>

            <div v-if="searchedMaterials.books.length > 0">
              <div class="flex justify-between items-center md:px-0 mb-4">
                <div
                  class="w-[228px] md:w-[262px] md:pr-6 lg:w-[287px] tracking-wide font-aktiv-grotesk-medium text-[20px] md:text-[24px] mt-10 leading-[32px] text-grey-90 font-bold"
                >
                  {{
                    $t("copy.resourcesPage.resourceSearch.bookSection.header")
                  }}
                  <in-app-browser
                    :href="bookUrl"
                    data-event-tracking="book-generations-volume-1"
                    target="_blank"
                  >
                    <img
                      class="w-full rounded-[16px] cursor-pointer mt-4"
                      :src="searchedMaterials.books[0].thumbnail"
                    />
                  </in-app-browser>
                </div>
              </div>
            </div>

            <div
              v-if="searchedMaterials.quotes.length > 0 && loadedQuotes"
              class="mt-[60px] pb-[87px] w-[calc(100vw-24px)] lg:w-[calc(100vw-500px)] bg-[#F8F9F9]"
            >
              <h1
                class="mb-[16px] font-aktiv-grotesk text-[24px] leading-[32px] font-bold"
              >
                {{ $t("copy.resourcesPage.quotesSection.header") }}
                <ResourcesSlider
                  :category-index="i"
                  category="quotes"
                  :items="searchedMaterials.quotes"
                />
              </h1>
            </div>

            <div v-if="getResultCount === 0">
              <p
                v-if="searchSuggestions && searchSuggestions.length > 0"
                class="font-aktiv-grotesk font-bold text-xl leading-[32px] mb-[16px]"
              >
                {{
                  $t(
                    "copy.resourcesPage.resourceSearch.suggestionSection.header"
                  )
                }}
              </p>
              <div class="flex items-start flex-wrap gap-2">
                <div
                  v-for="suggestion in searchSuggestions"
                  :key="suggestion.id"
                >
                  <div
                    class="rounded-full border-black border-2 text-black px-4 py-1 cursor-pointer"
                    :data-event-tracking="'suggestion-' + suggestion.keyword"
                    @click="useSuggestion(suggestion.keyword)"
                  >
                    {{ suggestion.keyword }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!isSearching && !isLoading">
          <p
            v-if="searchSuggestions && searchSuggestions.length > 0"
            class="font-aktiv-grotesk font-bold text-[20px] md:text-[24px] leading-[24px] md:leading-[32px] mt-6"
          >
            {{
              $t("copy.resourcesPage.resourceSearch.suggestionSection.header")
            }}
          </p>
          <div class="flex items-start flex-wrap gap-3 mt-6">
            <div v-for="suggestion in searchSuggestions" :key="suggestion.id">
              <div
                class="rounded-full border-black border-2 text-black px-4 py-1 cursor-pointer"
                @click="useSuggestion(suggestion.keyword)"
              >
                {{ suggestion.keyword }}
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="isLoading"
          class="flex justify-center items-center mt-[100px]"
        >
          <LoadingState />
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import SessionSearch from "@/components/ResourcesPage/SessionSearch.vue";
import { mapGetters, mapState } from "vuex";
import ResourceCard from "@/components/ResourcesPage/Results/ResourceCard.vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import ResourcesSlider from "@/components/ResourcesPage/PlaybackResources/ResourcesSlider.vue";
import { isIosNativeOnly, isAndroidNativeOnly } from "@/utils/platform-check";
import LoadingState from "@/components/common/LoadingState";

export default {
  name: "ResourceSearch",
  components: {
    SessionSearch,
    ResourceCard,
    Swiper,
    SwiperSlide,
    ResourcesSlider,
    LoadingState,
  },
  data() {
    return {
      nonDuplicateResources: [],
      flattenedMaterials: {
        pdf: [],
        links: [],
        books: [],
        quotes: [],
      },
      searchedMaterials: {
        pdf: [],
        links: [],
        books: [],
        quotes: [],
      },
      bookMaterials: [],
      quotesMaterials: [],
      currentLanguageSelect: {
        displayText: "English",
        code: "en",
      },
      swiperOptions: {
        centeredSlides: false,
        slidesPerView: "auto",
        grabCursor: true,
        pagination: {
          el: ".swiper-pagination",
        },
      },
      swipers: {},
      materialsCount: 0,
      resultsCount: 0,
      sessions: [],
      images: [],
      searchTerm: "",
      isSearching: false,
      loadedQuotes: false,
      isLoading: false,
    };
  },
  watch: {
    searchTerm(newTerm) {
      this.$router.push({
        path: "/resources/sessions/search", // Ensure this matches the name defined in your router
        query: { search: searchTerm },
      });
    },
  },
  computed: {
    ...mapState("user", ["token"]),
    ...mapState("cms", ["sessionMaterials", "loading"]),
    ...mapState("filter", [
      "searchSuggestions",
      "allResources",
      "flattenedBooks",
    ]),
    ...mapState("player", ["playingPlayer"]),
    isIos() {
      return isIosNativeOnly();
    },
    isAndroid() {
      return isAndroidNativeOnly();
    },
    getAssets() {
      if (this.searchTerm) {
        return this.searchedMaterials;
      }
      return this.flattenedMaterials;
    },
    getResultCount() {
      if (this.searchTerm) {
        return this.resultsCount;
      }
      return this.materialsCount;
    },
    bookUrl() {
      if (this.searchedMaterials.books) {
        return this.searchedMaterials.books[0].resourceTranslations[0]
          .resourceUrl;
      }
      return "";
    },
    searchResultsCopy() {
      if (this.getResultCount > 0) {
        return this.$t("copy.resourcesPage.searchContent.searchingStringText", {
          searchTerm: this.searchTerm,
        });
      } else {
        return this.$t(
          "copy.resourcesPage.searchContent.noResourcesSearchText",
          { searchTerm: this.searchTerm }
        );
      }
    },
  },
  async ionViewDidEnter() {
    this.isLoading = true;
    if (
      !this.allResources.length ||
      !this.searchSuggestions.length ||
      !this.flattenedBooks.length
    ) {
      const locale = "en";
      // use Promise.all to fetch all the data at once
      await Promise.all([
        this.$store.dispatch("filter/fetchAllResources", {
          locale: locale,
          conferenceType: "GIC",
        }),
        this.$store.dispatch("filter/fetchSearchSuggestions", {
          locale: locale,
          conferenceType: "GIC",
        }),
      ]);
    }
    this.searchTerm = this.$route.query.search ?? "";
    const combinedResources = this.allResources.flatMap(
      (resource) => resource.resources
    );
    const noDuplicateResources =
      this.RemoveDuplicatedResources(combinedResources);

    this.flattenedMaterials.pdf = noDuplicateResources.filter(
      (resource) => resource.type == "sm_tools"
    );
    this.materialsCount += this.flattenedMaterials.pdf.length;

    this.materialsCount += this.flattenedMaterials.quotes.length;
    this.loadedQuotes = true;

    this.flattenedMaterials.books = this.flattenedBooks;
    this.materialsCount += this.flattenedMaterials.books.length;

    this.flattenedMaterials.links = noDuplicateResources.filter(
      (resource) =>
        resource.type !== "sm_quotes" && resource.type !== "sm_tools"
    );
    this.materialsCount += this.flattenedMaterials.links.length;
    this.isLoading = false;
  },
  async ionViewWillLeave() {
    if (this.playingPlayer) {
      await this.playingPlayer.pauseVideo();
    }
  },
  methods: {
    RemoveDuplicatedResources(resources) {
      return resources.filter((resource, index, self) => {
        const firstIndex = self.findIndex(
          (r) => r.title === resource.title && r.type === resource.type
        );
        return firstIndex === index;
      });
    },

    onSearch(searchResult, searchTerm, resultsCount, isSearching) {
      this.isSearching = true;
      this.searchedMaterials.pdf = searchResult.pdf;
      this.searchedMaterials.links = searchResult.links;
      this.searchedMaterials.books = searchResult.books;
      this.searchedMaterials.quotes = searchResult.quotes;
      this.searchTerm = searchTerm;
      this.resultsCount = resultsCount;
      this.isSearching = isSearching;
      this.loadedQuotes = false;
      this.$router.push({
        path: "/resources/sessions/search",
        query: { search: searchTerm },
      });
      setTimeout(() => {
        this.loadedQuotes = true;
      }, 100);
    },
    useSuggestion(suggestionName) {
      this.searchTerm = suggestionName;
    },
    isYoutubeLink(url) {
      return url?.includes("youtube") || url?.includes("youtu.be");
    },
    back() {
      this.isSearching = false;
      this.$router.push("/resources/sessions");
    },
    setSwiperInstance(instance, header) {
      this.swipers[header] = instance;
    },
  },
};
</script>
<style scoped>
.tool-shadow {
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
}

.shadow {
  box-shadow: 0px 1px 25px 0px rgba(0, 0, 0, 0.25);
}

.swiper-slide {
  max-width: 328px;
  align-items: flex-start;
  vertical-align: middle;
  border-radius: 10px;
  display: flex;
  -webkit-transition: all 0.4s ease;
  transition: all 0.4s ease;
}

.swiper-container {
  list-style: none;
  z-index: 1;
  padding-bottom: 0px;
}

.download-button svg {
  fill: #00aae4;
}

.download-button:active svg {
  fill: #ffffff;
}

@media all and (min-width: 768px) {
  ::v-deep(.swiper-wrapper) {
    padding-left: 0;
  }
}
</style>
